import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './GamesResult.css';

// ======================
// Helper: Get Week Number
// ======================
function getWeekNumber(d) {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

const GamesResult = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ amount: 0, payout: 0 });
  const [selectedDate, setSelectedDate] = useState(null);
  const [weekFilter, setWeekFilter] = useState("this");

  useEffect(() => {
    const fetchData = async () => {
      const username = localStorage.getItem('username');
      if (!username) return;

      try {
        const betsQuery = query(
          collection(db, 'userBets'),
          where('username', '==', username)
        );
        const betsSnapshot = await getDocs(betsQuery);

        const fetchedBets = betsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const today = new Date();
        const currentWeek = getWeekNumber(today);
        const currentYear = today.getFullYear();

        const filteredBets = fetchedBets.filter(bet => {
          const betDate = new Date(bet.betTime);
          const betWeek = getWeekNumber(betDate);
          const betYear = betDate.getFullYear();

          if (weekFilter === 'this') return betWeek === currentWeek && betYear === currentYear;
          if (weekFilter === 'last') return betWeek === currentWeek - 1 && betYear === currentYear;
          if (weekFilter === 'year') return betYear === currentYear;
          if (weekFilter === 'lastYear') return betYear === currentYear - 1;
          return true;
        });

        const grouped = filteredBets.reduce((acc, bet) => {
          const date = new Date(bet.betTime).toISOString().split("T")[0];
          if (!acc[date]) acc[date] = { totalAmount: 0, totalPayout: 0, items: [] };
          acc[date].totalAmount += Number(bet.betAmount);
          acc[date].totalPayout += Number(bet.payout) - Number(bet.betAmount);
          acc[date].items.push(bet);
          return acc;
        }, {});

        const rowsArr = Object.keys(grouped).map(date => ({
          date,
          totalAmount: grouped[date].totalAmount,
          totalPayout: grouped[date].totalPayout,
          items: grouped[date].items
        }));

        rowsArr.sort((a, b) => new Date(b.date) - new Date(a.date));

        const totalAmount = rowsArr.reduce((s, r) => s + r.totalAmount, 0);
        const totalPayout = rowsArr.reduce((s, r) => s + r.totalPayout, 0);

        setRows(rowsArr);
        setTotals({ amount: totalAmount, payout: totalPayout });

      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };

    fetchData();
  }, [weekFilter]);

  const getRowColor = (row) => {
    if (row.items.every(bet => bet.status === "Lost")) return "red";
    if (row.items.some(bet => bet.status === "Won")) return "green";
    return "white";
  };

  return (
    <div className="betting-wrapper">

      {/* 🔹 Filter Banner */}
      <div className="filter-banner">
        <button className={weekFilter === "this" ? "active" : ""} onClick={() => setWeekFilter("this")}>
          Week hii
        </button>
        <button className={weekFilter === "last" ? "active" : ""} onClick={() => setWeekFilter("last")}>
          Week iliyo pita
        </button>
        <button className={weekFilter === "year" ? "active" : ""} onClick={() => setWeekFilter("year")}>
          mwaka huu
        </button>
      </div>

      <table className="records-table">
        <tbody>
          {rows.map((r) => {
            const isOpen = selectedDate === r.date;

            return (
              <React.Fragment key={r.date}>
                <tr onClick={() => setSelectedDate(isOpen ? null : r.date)} className="clickable">
                  <td className="arrow">{isOpen ? "Click" : "▶Click"}</td>
                  <td>{r.date}</td>
                  <td>{r.totalAmount.toLocaleString()}</td>
                  <td className={getRowColor(r)}>
                    {r.totalPayout.toLocaleString()}
                  </td>
                </tr>

                {/* ================================
                    🔥 FULL MATCH DETAILS (FINAL SCORE INCLUDED)
                ================================= */}
                {isOpen && (
                  <tr>
                    <td colSpan="4">
                      {r.items.map(bet => {
                        let statusClass = "white";
                        if (bet.status === "Won") statusClass = "green";
                        if (bet.status === "Lost") statusClass = "red";

                        return (
                          <div key={bet.id} className="match-details">

                            {/* STATUS */}
                            <p className={`status-tag ${statusClass}`}>
                              {bet.status === "Won" ? "WON" : "LOST"}
                            </p>

                            {/* TEAMS */}
                            <p><strong>{bet.teamA}</strong> VS <strong>{bet.teamB}</strong></p>

                            {/* TIME */}
                            <p>{new Date(bet.betTime).toLocaleString()}</p>

                            {/* USER PICK */}
                            <p>
                              Your Pick:
                              <span className="score"> {bet.selectedScore} </span>
                              <span className="odds"> odd {Number(bet.odds).toFixed(2)}</span>
                            </p>

                            {/* ⭐ FINAL SCORE */}
                            <p>
                              Final Score:
                              <span className="final-score">
                                {bet.finalScore ? bet.finalScore : "—"}
                              </span>
                            </p>

                            {/* AMOUNT */}
                            <p>
                              Amount:
                              <span className={statusClass}>
                                {Number(bet.betAmount).toLocaleString()} {bet.currency}
                              </span>
                            </p>

                            {/* PROFIT */}
                            <p>
                              Profit:
                              <span className={statusClass}>
                                {(Number(bet.payout) - Number(bet.betAmount)).toLocaleString()} {bet.currency}
                              </span>
                            </p>

                            <hr />
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}

          {/* SUMMARY */}
          <tr className="summary">
            <td></td>
            <td><strong>Total</strong></td>
            <td><strong>{totals.amount.toLocaleString()}</strong></td>
            <td className={rows.every(r => r.items.every(b => b.status === "Lost")) ? "red" : "green"}>
              <strong>{totals.payout.toLocaleString()}</strong>
            </td>
          </tr>

        </tbody>
      </table>
    </div>
  );
};

export default GamesResult;